import {
  createElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type ReactNode,
} from 'react';
import { Bird, CalendarDays, Cat, CircleHelp, Dog, Rabbit } from 'lucide-react';
import { FarmAnimalIcon } from '../../ui';
import { phoneCountryForCity } from '../../features/requestWizard/phoneCountry';
import { requestToday } from '../../features/requestWizard/requestDates';
import { localizeSmsError } from '../../features/registration/smsError';

import { api, ApiError } from '../../api/client';
import { useFigmaBreakpoint, type FigmaBreakpoint } from '../../figma/useFigmaBreakpoint';
import {
  clearPendingPhotos,
  emptyRequestDraft,
  getPendingPhotos,
  removePendingPhoto,
  storePendingPhoto,
  useRequestDraft,
  type PendingPhoto,
  type PetType,
  type RequestDraft,
} from '../../features/requestWizard/model';
import { hashQuery, loadPendingYandexRegistration } from '../../features/auth/yandex';
import { loadPendingVkRegistration } from '../../features/auth/vk';
import { SocialAuthButtons } from '../../features/auth/SocialAuthButtons';
import { loadPendingOidc } from '../../features/auth/oidc';
import {
  publishRequest,
  saveRequestOnServer,
  uploadRequestPhotos,
} from '../../features/requestWizard/api';
import { RequestProgress } from '../../features/requestWizard/RequestProgress';
import { getDisplayCurrency } from '../../i18n/preferences';
import { useAuth, type SessionUser } from '../../state/auth';
import { Button, Chip, Footer, Header, Input, InternationalPhoneInput, MobileDrawer, Tag, Tile, type LovoButtonProps } from '../../ui';
import type { ScreenId } from '../../app/screenRoutes';
import './request-wizard.css';
import './request-wizard-responsive.css';

function WizardButton({ size = 'medium', ...props }: LovoButtonProps) {
  return <Button size={size} {...props} />;
}

type DraftUpdater = (patch: Partial<RequestDraft>) => void;

interface WizardStepProps {
  draft: RequestDraft;
  error: string;
  setError: (message: string) => void;
  updateDraft: DraftUpdater;
}

const ROOT_NODES: Partial<Record<ScreenId, Partial<Record<FigmaBreakpoint, string>>>> = {
  'new-request-step-1': {
    '1920': '173:45917',
    '1440': '173:45867',
    '768': '173:45713',
    '375': '173:45661',
  },
  'new-request-step-2': {
    '1920': '176:45918',
    '1440': '176:45954',
    '768': '176:46026',
    '375': '176:45990',
  },
  'new-request-step-3': { '1920': '179:48503', '1440': '2535:17345' },
  'new-request-step-4': { '1920': '2721:50914', '1440': '2723:51276' },
  'new-request-step-5': { '1920': '2723:52400', '1440': '2723:52647' },
  'new-request-step-6': { '1920': '2723:51659', '1440': '2723:52016' },
  'new-request-step-7': { '1920': '2736:36234', '1440': '2736:36500' },
  'new-request-step-8': { '1920': '2736:36900', '1440': '2736:37148' },
  'new-request-step-9': { '1920': '2736:38341', '1440': '2736:38865' },
  'new-request-step-9-role': { '1920': '2736:39194', '1440': '2736:39369' },
  'new-request-step-9-name': { '1920': '2736:39694', '1440': '2736:39910' },
  'new-request-step-9-name-company': { '1920': '2736:40222', '1440': '2736:40378' },
  'new-request-step-9-sent': { '1920': '2736:40712', '1440': '2736:40912' },
  'new-request-step-9-password': { '1920': '2736:41245', '1440': '2736:41466' },
  'new-request-step-10': { '1920': '2736:37630', '1440': '2736:38087' },
};

const WIZARD_STEP: Partial<Record<ScreenId, number>> = {
  'new-request-step-1': 1,
  'new-request-step-2': 2,
  'new-request-step-3': 3,
  'new-request-step-4': 4,
  'new-request-step-5': 5,
  'new-request-step-6': 6,
  'new-request-step-7': 7,
  'new-request-step-8': 8,
  'new-request-step-9': 9,
  'new-request-step-9-role': 9,
  'new-request-step-9-name': 9,
  'new-request-step-9-name-company': 9,
  'new-request-step-9-sent': 9,
  'new-request-step-9-password': 9,
  'new-request-step-10': 9,
};

const WIZARD_LABEL: Partial<Record<ScreenId, string>> = {
  'new-request-step-1': 'Маршрут',
  'new-request-step-2': 'Питомец',
  'new-request-step-3': 'Сроки',
  'new-request-step-4': 'Условия',
  'new-request-step-5': 'Контакты',
  'new-request-step-6': 'Бюджет',
  'new-request-step-7': 'Фото',
  'new-request-step-8': 'Проверка',
  'new-request-step-9': 'Завершение',
  'new-request-step-9-role': 'Завершение',
  'new-request-step-9-name': 'Завершение',
  'new-request-step-9-name-company': 'Завершение',
  'new-request-step-9-sent': 'Завершение',
  'new-request-step-9-password': 'Завершение',
  'new-request-step-10': 'Завершение',
};

const PREVIOUS_SCREEN: Partial<Record<ScreenId, ScreenId>> = {
  'new-request-step-2': 'new-request-step-1',
  'new-request-step-3': 'new-request-step-2',
  'new-request-step-4': 'new-request-step-3',
  'new-request-step-5': 'new-request-step-4',
  'new-request-step-6': 'new-request-step-5',
  'new-request-step-7': 'new-request-step-6',
  'new-request-step-8': 'new-request-step-7',
  'new-request-step-9': 'new-request-step-8',
  'new-request-step-9-role': 'new-request-step-9',
  'new-request-step-9-name': 'new-request-step-9-role',
  'new-request-step-9-name-company': 'new-request-step-9-role',
  'new-request-step-9-sent': 'new-request-step-9-name',
  'new-request-step-9-password': 'new-request-step-9-sent',
};

function navigate(screen: ScreenId): void {
  window.location.hash = `#${screen}`;
  window.scrollTo({ top: 0, behavior: 'auto' });
}

function messageFromError(error: unknown): string {
  let msg = '';
  if (error instanceof ApiError) {
    if (typeof error.payload === 'object' && error.payload) {
      const first = Object.values(error.payload as Record<string, unknown>)[0];
      if (Array.isArray(first) && first.length) msg = String(first[0]);
      else if (typeof first === 'string') msg = first;
    }
    if (!msg) msg = error.message;
  } else {
    msg = error instanceof Error ? error.message : String(error);
  }
  return msg.replace(/\.+$/, '');
}

function WizardShell({
  children,
  screen,
}: {
  children: ReactNode;
  screen: ScreenId;
}) {
  const { status, user } = useAuth();
  const breakpoint = useFigmaBreakpoint();
  const node = ROOT_NODES[screen]?.[breakpoint];
  const [drawerOpen, setDrawerOpen] = useState(false);
  const step = WIZARD_STEP[screen] ?? 1;
  const label = WIZARD_LABEL[screen] ?? '';
  const previous = PREVIOUS_SCREEN[screen];
  const isAuthenticated = status === 'authenticated' && Boolean(user);
  const accountLabel = user?.display_name?.trim() || user?.email || 'Личный кабинет';
  const showDetailedAuthenticatedHeader = isAuthenticated && breakpoint === '1920' && [
    'new-request-step-4',
    'new-request-step-5',
    'new-request-step-6',
    'new-request-step-7',
    'new-request-step-8',
    'new-request-step-10',
  ].includes(screen);
  const authenticatedActions = (
    <div className="request-wizard__header-actions">
      <button type="button" aria-label="Сообщения" onClick={() => navigate('chat-list')}>
        <img className="request-wizard__header-message" src="/figma/assets/profile-message-icon.svg" alt="" aria-hidden="true" />
        <img className="request-wizard__header-dot" src="/figma/assets/profile-notification-dot.svg" alt="" aria-hidden="true" />
      </button>
      <button type="button" aria-label="Уведомления" onClick={() => navigate('notifications')}>
        <img className="request-wizard__header-bell" src="/figma/assets/profile-bell-icon.svg" alt="" aria-hidden="true" />
        <img className="request-wizard__header-bell-bottom" src="/figma/assets/profile-bell-bottom.svg" alt="" aria-hidden="true" />
        <img className="request-wizard__header-dot" src="/figma/assets/profile-notification-dot.svg" alt="" aria-hidden="true" />
      </button>
    </div>
  );
  const mobileHeaderActions = screen === 'new-request-step-2' ? (
    isAuthenticated ? authenticatedActions : undefined
  ) : undefined;

  return (
    <section
      className={`request-wizard request-wizard--${screen}`}
      data-screen={screen}
      data-figma-node={node}
      data-figma-variant={breakpoint}
    >
      <div className="request-wizard__stage">
        <Header
          authenticated={isAuthenticated}
          actions={showDetailedAuthenticatedHeader ? authenticatedActions : undefined}
          account={showDetailedAuthenticatedHeader ? (
            <button className="request-wizard__account" type="button" onClick={() => navigate('profile-internal')}>
              <span>{accountLabel}</span>
              <img src="/figma/assets/profile-arrow-up-right.svg" alt="" aria-hidden="true" />
            </button>
          ) : undefined}
          onLogoClick={() => navigate('landing')}
          onLogin={() => navigate('new-request-step-9')}
          onCarrierRegistration={() => navigate('carrier-rega-email')}
          onMenuToggle={() => setDrawerOpen(true)}
          menuExpanded={drawerOpen}
          externalDrawer
          mobileActions={mobileHeaderActions}
        />
        {drawerOpen && (
          <MobileDrawer
            variant={isAuthenticated ? 'owner' : 'no log in'}
            onClose={() => setDrawerOpen(false)}
            onLogoClick={() => navigate('landing')}
            onNavigate={(action) => {
              setDrawerOpen(false);
              if (action === 'login') navigate('new-request-step-9');
              if (action === 'carrier-registration') navigate('carrier-rega-email');
            }}
          />
        )}
        <div className="request-wizard__layout">
          <div
            className="request-wizard__image"
            role="img"
            aria-label="Дорога к месту назначения"
            data-figma-node="124:31954"
          />
          <main className="request-wizard__form-shell">
            <RequestProgress
              current={step}
              label={label}
              onBack={() => navigate(previous ?? 'landing')}
            />
            {children}
          </main>
        </div>
      </div>
      <Footer
        legalDetails={{
          legalName: 'ООО/ИП “ХХХ”',
          inn: '123456789012',
          ogrnip: '12345678901234567',
          year: 2026,
        }}
        onNavigate={(action) => {
          if (action === 'logo') navigate('landing');
          if (action === 'customer-cabinet') navigate('my-requests');
          if (action === 'carrier-cabinet') navigate('carrier-dashboard');
          if (action === 'carrier-registration') navigate('carrier-rega-email');
        }}
      />
    </section>
  );
}

function StepFrame({
  actions,
  children,
  description,
  node,
  title,
}: {
  actions: ReactNode;
  children: ReactNode;
  description: string;
  node: string;
  title: string;
}) {
  return (
    <div className="request-wizard__step" data-figma-node={node}>
      <div className="request-wizard__step-content">
        <div className="request-wizard__copy">
          <h1>{title}</h1>
          <p>{description}</p>
        </div>
        {children}
      </div>
      <div className="request-wizard__actions">{actions}</div>
    </div>
  );
}

function StepError({ message }: { message: string }) {
  return message ? <p className="request-wizard__error" role="alert">{message.replace(/\.+$/, '')}</p> : null;
}

function RouteStep({ draft, error, setError, updateDraft }: WizardStepProps) {
  const routes = [
    ['Москва', 'Берлин'],
    ['Москва', 'Сочи'],
    ['СПб', 'Хельсинки'],
    ['Москва', 'Минск'],
    ['Москва', 'Алматы'],
  ] as const;

  const chooseRoute = (fromCity: string, toCity: string) => {
    updateDraft({ fromCity, toCity });
    setError('');
  };

  const next = () => {
    if (!draft.fromCity.trim() || !draft.toCity.trim()) {
      setError('Укажите оба города маршрута.');
      return;
    }
    navigate('new-request-step-2');
  };

  return (
    <StepFrame
      node="124:31974"
      title="Откуда и куда поедет питомец?"
      description="Введите два города или выберите популярное направление одним кликом"
      actions={<WizardButton icon="right" onClick={next}>Дальше</WizardButton>}
    >
      <div className="request-wizard__route-controls">
        <div className="request-wizard__input-stack">
          <Input
            density="compact"
            label="Откуда"
            icon={<img src="/figma/assets/landing-geo.svg" alt="" />}
            value={draft.fromCity}
            placeholder="Москва"
            autoComplete="address-level2"
            onChange={(event) => updateDraft({ fromCity: event.target.value })}
          />
          <Input
            density="compact"
            label="Куда"
            icon={<img src="/figma/assets/landing-geo.svg" alt="" />}
            value={draft.toCity}
            placeholder="Берлин"
            autoComplete="address-level2"
            onChange={(event) => updateDraft({ toCity: event.target.value })}
          />
        </div>
        <div className="request-wizard__route-presets">
          <h2>Популярные направления</h2>
          <div className="request-wizard__route-chips">
            {routes.map(([from, to], index) => {
              const active =
                (draft.fromCity === from && draft.toCity === to) ||
                (!draft.fromCity && !draft.toCity && index === 0);
              return (
                <Chip
                  key={`${from}-${to}`}
                  state={active ? 'choice' : 'Default'}
                  from={from}
                  to={to}
                  onClick={() => chooseRoute(from, to)}
                />
              );
            })}
          </div>
        </div>
      </div>
      <StepError message={error} />
    </StepFrame>
  );
}

const PET_TYPES: Array<{ value: PetType; label: string; icon: ReactNode }> = [
  { value: 'dog', label: 'Собака', icon: <Dog aria-hidden="true" size={24} strokeWidth={1.5} /> },
  { value: 'cat', label: 'Кошка', icon: <Cat aria-hidden="true" size={24} strokeWidth={1.5} /> },
  { value: 'rodent', label: 'Грызун', icon: <Rabbit aria-hidden="true" size={24} strokeWidth={1.5} /> },
  { value: 'bird', label: 'Птица', icon: <Bird aria-hidden="true" size={24} strokeWidth={1.5} /> },
  { value: 'farm', label: 'Сельхоз', icon: <FarmAnimalIcon aria-hidden="true" size={24} strokeWidth={1.5} /> },
  { value: 'other', label: 'Другое', icon: <CircleHelp aria-hidden="true" size={24} strokeWidth={1.5} /> },
];

function PetStep({ draft, error, setError, updateDraft }: WizardStepProps) {
  const next = () => {
    if (draft.petType === 'other' && !draft.customType.trim()) {
      setError('Опишите вид животного.');
      return;
    }
    setError('');
    navigate('new-request-step-3');
  };

  return (
    <StepFrame
      node="2525:25792"
      title="Кого везём?"
      description="Тип животного, порода и основные параметры — это поможет перевозчику подготовиться."
      actions={<WizardButton icon="right" onClick={next}>Дальше</WizardButton>}
    >
      <div className="request-wizard__pet-fields">
        <section className="request-wizard__pet-types">
          <h2>Тип животного</h2>
          <div className="request-wizard__pet-chips">
            {PET_TYPES.map((pet) => (
              <Chip
                className="request-wizard__pet-chip"
                key={pet.value}
                state={draft.petType === pet.value ? 'State4' : 'State3'}
                label={pet.label}
                icon={pet.icon}
                onClick={() => updateDraft({ petType: pet.value })}
              />
            ))}
          </div>
          {draft.petType === 'other' && <Input
            density="compact"
            label="Если выбрали «Другое», опишите вид"
            placeholder="Например, черепаха красноухая"
            value={draft.customType}
            onChange={(event) => updateDraft({ customType: event.target.value })}
          />}
        </section>
        <div className="request-wizard__pet-details">
          <div className="request-wizard__field-row">
            <Input density="compact" label="Порода" placeholder="Например, лабрадор" value={draft.breed} onChange={(event) => updateDraft({ breed: event.target.value })} />
            <Input density="compact" label="Количество" type="number" min={1} value={draft.count} onChange={(event) => updateDraft({ count: Number(event.target.value) || 1 })} />
          </div>
          <div className="request-wizard__field-row">
            <Input density="compact" label="Вес, кг" type="number" min={0} step="0.1" placeholder="25" value={draft.weightKg} onChange={(event) => updateDraft({ weightKg: event.target.value })} />
            <Input density="compact" label="Возраст" placeholder="3 года" value={draft.age} onChange={(event) => updateDraft({ age: event.target.value })} />
          </div>
          <Input density="compact" label="Кличка (опционально)" placeholder="Барон" value={draft.petName} onChange={(event) => updateDraft({ petName: event.target.value })} />
        </div>
      </div>
      <StepError message={error} />
    </StepFrame>
  );
}

function DateStep({ draft, error, setError, updateDraft }: WizardStepProps) {
  const todayStr = requestToday();
  const currentYear = Number(todayStr.slice(0, 4));
  const maxYear = useMemo(() => currentYear + 1, [currentYear]);
  const maxDateStr = useMemo(() => `${maxYear}-12-31`, [maxYear]);

  const sanitizeDate = useCallback((val: string): string => {
    if (!val) return '';
    const parts = val.split('-');
    if (parts.length > 0 && parts[0].length > 4) {
      parts[0] = parts[0].slice(0, 4);
    }
    if (parts.length > 0 && parts[0].length === 4) {
      const year = Number(parts[0]);
      if (year > maxYear) parts[0] = String(maxYear);
    }
    return parts.join('-');
  }, [maxYear]);

  const normalizeOnBlur = useCallback((val: string, field: 'pickupNotBefore' | 'deliveryNotAfter') => {
    if (!val) return;
    const parts = val.split('-');
    if (parts.length === 3 && parts[0].length === 4) {
      const year = Number(parts[0]);
      if (year < currentYear) {
        parts[0] = String(currentYear);
        const corrected = parts.join('-');
        const finalVal = corrected < todayStr ? todayStr : corrected;
        updateDraft({ [field]: finalVal });
      }
    }
  }, [currentYear, todayStr, updateDraft]);

  const urgency = [
    ['urgent', 'Срочная', 'В ближайшие 2 дня'],
    ['normal', 'Обычная', 'В течение недели'],
    ['flexible', 'Можно подождать', 'Более 2 недель'],
  ] as const;

  const next = () => {
    if (!draft.pickupNotBefore || !draft.deliveryNotAfter) {
      setError('Укажите даты забора и доставки.');
      return;
    }

    const pickupYear = Number(draft.pickupNotBefore.slice(0, 4));
    const deliveryYear = Number(draft.deliveryNotAfter.slice(0, 4));

    if (isNaN(pickupYear) || pickupYear < currentYear || pickupYear > maxYear) {
      setError(`Укажите корректный год для даты забора (${currentYear}–${maxYear}).`);
      return;
    }
    if (isNaN(deliveryYear) || deliveryYear < currentYear || deliveryYear > maxYear) {
      setError(`Укажите корректный год для даты доставки (${currentYear}–${maxYear}).`);
      return;
    }

    if (draft.pickupNotBefore < todayStr) {
      setError('Дата забора не может быть в прошлом.');
      return;
    }

    if (draft.deliveryNotAfter < draft.pickupNotBefore) {
      setError('Дата доставки не может быть раньше даты забора.');
      return;
    }

    setError('');
    navigate('new-request-step-4');
  };

  return (
    <StepFrame
      node="124:33686"
      title="Когда нужно ехать?"
      description="Чем шире диапазон дат — тем больше откликов и ниже средняя цена"
      actions={<WizardButton icon="right" onClick={next}>Дальше</WizardButton>}
    >
      <div className="request-wizard__date-fields">
        <div className="request-wizard__field-row">
          <Input
            density="compact"
            label="Забрать не раньше"
            icon={<CalendarDays aria-hidden="true" />}
            type="date"
            min={todayStr}
            max={maxDateStr}
            value={draft.pickupNotBefore}
            onBlur={(event) => normalizeOnBlur(event.target.value, 'pickupNotBefore')}
            onChange={(event) => {
              const val = sanitizeDate(event.target.value);
              setError('');
              updateDraft({ pickupNotBefore: val });
            }}
          />
          <Input
            density="compact"
            label="Доставить не позже"
            icon={<CalendarDays aria-hidden="true" />}
            type="date"
            min={draft.pickupNotBefore && draft.pickupNotBefore > todayStr ? draft.pickupNotBefore : todayStr}
            max={maxDateStr}
            value={draft.deliveryNotAfter}
            onBlur={(event) => normalizeOnBlur(event.target.value, 'deliveryNotAfter')}
            onChange={(event) => {
              const val = sanitizeDate(event.target.value);
              setError('');
              updateDraft({ deliveryNotAfter: val });
            }}
          />
        </div>
        <StepError message={error} />
        <section className="request-wizard__urgency">
          <h2>Срочность</h2>
          <div className="request-wizard__urgency-row">
            {urgency.map(([value, title, description]) => (
              <Chip
                className={draft.urgency === value ? 'is-selected' : ''}
                key={value}
                state="State5"
                title={title}
                description={description}
                onClick={() => updateDraft({ urgency: value })}
              />
            ))}
          </div>
        </section>
      </div>
    </StepFrame>
  );
}

function ConditionsStep({ draft, error, setError, updateDraft }: WizardStepProps) {
  return (
    <StepFrame
      node="2721:50930"
      title="Что важно знать перевозчику"
      description="Переноска, документы, привычки питомца — мелочи, на которых строится спокойная поездка"
      actions={<WizardButton icon="right" onClick={() => { setError(''); navigate('new-request-step-5'); }}>Дальше</WizardButton>}
    >
      <div className="request-wizard__conditions">
        <ChoiceBlock label="Переноска / клетка">
          <Tile className="request-wizard__condition-tile" active={draft.carrierBox === 'own_box'} title="У меня есть" description="Своя переноска" onClick={() => updateDraft({ carrierBox: 'own_box' })} />
          <Tile className="request-wizard__condition-tile" active={draft.carrierBox === 'carrier_box'} title="Нужна от перевозчика" description="Готов оплатить" onClick={() => updateDraft({ carrierBox: 'carrier_box' })} />
        </ChoiceBlock>
        <ChoiceBlock label="Ветпаспорт и прививки">
          <Tile className="request-wizard__condition-tile" active={draft.documentsStatus === 'ready'} title="Все документы есть" description="Прививки актуальны" onClick={() => updateDraft({ documentsStatus: 'ready' })} />
          <Tile className="request-wizard__condition-tile" active={draft.documentsStatus === 'in_progress'} title="Документы оформляются" description="Нужна помощь" onClick={() => updateDraft({ documentsStatus: 'in_progress' })} />
        </ChoiceBlock>
        <label className="request-wizard__textarea-field">
          <span>Особенности животного</span>
          <textarea value={draft.petNotes} placeholder="Например: боится громких звуков, есть аллергия на курицу, требует прогулок каждые 4 часа..." onChange={(event) => updateDraft({ petNotes: event.target.value })} />
        </label>
        <ChoiceBlock label="Кормление в пути" compact>
          <Tile className="request-wizard__feeding-tile" active={draft.feedingNotes === 'owner_food'} title="Дам корм с собой" onClick={() => updateDraft({ feedingNotes: 'owner_food' })} />
          <Tile className="request-wizard__feeding-tile" active={draft.feedingNotes === 'carrier_food'} title="Кормит перевозчик" onClick={() => updateDraft({ feedingNotes: 'carrier_food' })} />
        </ChoiceBlock>
        <p className="request-wizard__hint">Рекомендуем давать корм с собой — питомцу не придётся адаптироваться к новой еде в стрессовой поездке.</p>
      </div>
      <StepError message={error} />
    </StepFrame>
  );
}

function ChoiceBlock({ children, compact = false, label }: { children: ReactNode; compact?: boolean; label: string }) {
  return (
    <section className={`request-wizard__choice-block${compact ? ' request-wizard__choice-block--compact' : ''}`}>
      <h2>{label}</h2>
      <div>{children}</div>
    </section>
  );
}

function digitsCount(phone: string): number {
  return phone.replace(/\D/g, '').length;
}

function ContactStep({ draft, updateDraft }: WizardStepProps) {
  const [fieldErrors, setFieldErrors] = useState<{
    pickupContactName?: string;
    pickupContactPhone?: string;
    deliveryContactName?: string;
    deliveryContactPhone?: string;
  }>({});

  const next = () => {
    const nextErrors: typeof fieldErrors = {};
    if (!draft.pickupContactName.trim()) {
      nextErrors.pickupContactName = 'Укажите контактное лицо';
    }

    if (!draft.pickupContactPhone.trim()) {
      nextErrors.pickupContactPhone = 'Укажите телефон';
    } else if (digitsCount(draft.pickupContactPhone) < 6) {
      nextErrors.pickupContactPhone = 'Введите корректный номер телефона';
    }

    if (!draft.deliveryContactName.trim()) {
      nextErrors.deliveryContactName = 'Укажите получателя';
    }

    if (!draft.deliveryContactPhone.trim()) {
      nextErrors.deliveryContactPhone = 'Укажите телефон получателя';
    } else if (digitsCount(draft.deliveryContactPhone) < 6) {
      nextErrors.deliveryContactPhone = 'Введите корректный номер телефона';
    }

    if (Object.keys(nextErrors).length > 0) {
      setFieldErrors(nextErrors);
      return;
    }
    setFieldErrors({});
    navigate('new-request-step-6');
  };

  return (
    <StepFrame
      node="2723:52568"
      title="Кто встретит и передаст"
      description="Точный адрес скажете перевозчику в чате после выбора — здесь только контакты и ориентир по району. Так безопаснее."
      actions={<WizardButton icon="right" onClick={next}>Дальше</WizardButton>}
    >
      <div className="request-wizard__contact-cards">
        <ContactCard title={`Где забрать · город ${draft.fromCity || 'Москва'}`}>
          <Input
            density="compact"
            label="Контактное лицо"
            placeholder="Анна"
            autoComplete="name"
            error={fieldErrors.pickupContactName}
            value={draft.pickupContactName}
            onChange={(event) => {
              updateDraft({ pickupContactName: event.target.value });
              if (fieldErrors.pickupContactName) setFieldErrors((prev) => ({ ...prev, pickupContactName: undefined }));
            }}
          />
          <InternationalPhoneInput
            density="compact"
            label="Телефон"
            error={fieldErrors.pickupContactPhone}
            defaultCountry={phoneCountryForCity(draft.fromCity)}
            value={draft.pickupContactPhone}
            onChange={(phone) => {
              updateDraft({ pickupContactPhone: phone });
              if (fieldErrors.pickupContactPhone) setFieldErrors((prev) => ({ ...prev, pickupContactPhone: undefined }));
            }}
          />
          <label className="request-wizard__contact-notes">
            <span>Ориентир по району (опционально)</span>
            <textarea placeholder="Например: район Тверская, ближе к метро Маяковская. Удобнее после 10 утра." value={draft.pickupContactNotes} onChange={(event) => updateDraft({ pickupContactNotes: event.target.value })} />
          </label>
        </ContactCard>
        <ContactCard title={`Куда доставить · город ${draft.toCity || 'Сочи'}`}>
          <Input
            density="compact"
            label="Получатель"
            placeholder="Иван"
            autoComplete="name"
            error={fieldErrors.deliveryContactName}
            value={draft.deliveryContactName}
            onChange={(event) => {
              updateDraft({ deliveryContactName: event.target.value });
              if (fieldErrors.deliveryContactName) setFieldErrors((prev) => ({ ...prev, deliveryContactName: undefined }));
            }}
          />
          <InternationalPhoneInput
            density="compact"
            label="Телефон получателя"
            error={fieldErrors.deliveryContactPhone}
            defaultCountry={phoneCountryForCity(draft.toCity)}
            value={draft.deliveryContactPhone}
            onChange={(phone) => {
              updateDraft({ deliveryContactPhone: phone });
              if (fieldErrors.deliveryContactPhone) setFieldErrors((prev) => ({ ...prev, deliveryContactPhone: undefined }));
            }}
          />
          <label className="request-wizard__contact-notes">
            <span>Ориентир по району (опционально)</span>
            <textarea placeholder="Например: район Адлера, ближе к набережной." value={draft.deliveryContactNotes} onChange={(event) => updateDraft({ deliveryContactNotes: event.target.value })} />
          </label>
        </ContactCard>
      </div>
    </StepFrame>
  );
}

function ContactCard({ children, title }: { children: ReactNode; title: string }) {
  return (
    <section className="request-wizard__contact-card">
      <h2>{title}</h2>
      {children}
    </section>
  );
}

function BudgetStep({ draft, error, setError, updateDraft }: WizardStepProps) {
  const currencySymbol = getDisplayCurrency() === 'EUR' ? '€' : '₽';
  const next = () => {
    if (draft.budgetMode === 'fixed' && !draft.budgetMin && !draft.budgetMax) {
      setError('Укажите ориентир бюджета или выберите предложения перевозчиков.');
      return;
    }
    setError('');
    navigate('new-request-step-7');
  };

  return (
    <StepFrame
      node="2723:51762"
      title="Сколько готовы заплатить?"
      description="Можно задать ориентир — или оставить пусто, и перевозчики предложат свои цены"
      actions={<WizardButton icon="right" onClick={next}>Дальше</WizardButton>}
    >
      <div className="request-wizard__budget">
        <div className="request-wizard__budget-mode">
          <Tile className="request-wizard__budget-tile" active={draft.budgetMode === 'fixed'} title="Указать бюджет" description="Получите более точные отклики" onClick={() => updateDraft({ budgetMode: 'fixed' })} />
          <Tile className="request-wizard__budget-tile" active={draft.budgetMode === 'offers'} title="Пусть предложат" description="Сравните цены" onClick={() => updateDraft({ budgetMode: 'offers', budgetMin: '', budgetMax: '' })} />
        </div>
        <div className="request-wizard__field-row">
          <Input density="compact" label={`От, ${currencySymbol}`} inputMode="numeric" placeholder="12 000" value={draft.budgetMin} onChange={(event) => updateDraft({ budgetMin: event.target.value, budgetMode: 'fixed' })} />
          <Input density="compact" label={`До, ${currencySymbol}`} inputMode="numeric" placeholder="18 000" value={draft.budgetMax} onChange={(event) => updateDraft({ budgetMax: event.target.value, budgetMode: 'fixed' })} />
        </div>
      </div>
      <StepError message={error} />
    </StepFrame>
  );
}

interface PhotoPreview extends PendingPhoto {
  url: string;
}

function PhotoStep({ draft, error, setError, updateDraft }: WizardStepProps) {
  const [photos, setPhotos] = useState<PhotoPreview[]>([]);
  const fileInput = useRef<HTMLInputElement>(null);

  const refreshPhotos = useCallback(async () => {
    const stored = await getPendingPhotos(draft.photoKeys);
    setPhotos((current) => {
      current.forEach((photo) => URL.revokeObjectURL(photo.url));
      return stored.map((photo) => ({ ...photo, url: URL.createObjectURL(photo.blob) }));
    });
  }, [draft.photoKeys]);

  useEffect(() => {
    void refreshPhotos();
    return () => {
      setPhotos((current) => {
        current.forEach((photo) => URL.revokeObjectURL(photo.url));
        return [];
      });
    };
  }, [refreshPhotos]);

  const addFiles = async (files: FileList | File[]) => {
    const allowed = Array.from(files).filter((file) => ['image/jpeg', 'image/png', 'image/webp'].includes(file.type));
    const available = Math.max(0, 8 - draft.photoKeys.length);
    const selected = allowed.slice(0, available);
    if (!selected.length) {
      setError(available ? 'Поддерживаются JPG, PNG и WEBP.' : 'Можно загрузить не более 8 фото.');
      return;
    }
    if (selected.some((file) => file.size > 10 * 1024 * 1024)) {
      setError('Размер одного фото не должен превышать 10 МБ.');
      return;
    }
    try {
      const stored = await Promise.all(selected.map(storePendingPhoto));
      updateDraft({ photoKeys: [...draft.photoKeys, ...stored.map((photo) => photo.id)], photoAssetIds: [] });
      setError('');
    } catch {
      setError('Не удалось сохранить фото. Попробуйте выбрать файл ещё раз.');
    }
  };

  const removePhoto = async (id: string) => {
    await removePendingPhoto(id);
    updateDraft({ photoKeys: draft.photoKeys.filter((key) => key !== id), photoAssetIds: [] });
  };

  return (
    <StepFrame
      node="2736:36382"
      title="Покажите питомца"
      description="Перевозчик узнает животное при встрече и пришлёт фото в пути — будет с чем сравнить"
      actions={
        <div className="request-wizard__photo-actions">
          <WizardButton icon="right" onClick={() => navigate('new-request-step-8')}>Дальше</WizardButton>
          <WizardButton kind="secondary" onClick={() => navigate('new-request-step-8')}>Пропустить</WizardButton>
        </div>
      }
    >
      <div className="request-wizard__photo-fields">
        <button className="request-wizard__uploader" type="button" onClick={() => fileInput.current?.click()} onDragOver={(event) => event.preventDefault()} onDrop={(event) => { event.preventDefault(); void addFiles(event.dataTransfer.files); }}>
          <strong>Перетащите фото или нажмите, чтобы загрузить</strong>
          <span>JPG, PNG · до 10 МБ · до 8 фото</span>
        </button>
        <input ref={fileInput} className="request-wizard__file-input" type="file" accept="image/jpeg,image/png,image/webp" multiple onChange={(event: ChangeEvent<HTMLInputElement>) => { if (event.target.files) void addFiles(event.target.files); event.target.value = ''; }} />
        {photos.length > 0 ? (
          <div className="request-wizard__photo-grid">
            {photos.map((photo, index) => (
              <figure className="request-wizard__photo" key={photo.id}>
                <img src={photo.url} alt={`Фото питомца ${index + 1}`} />
                <button type="button" aria-label={`Удалить фото ${index + 1}`} onClick={() => void removePhoto(photo.id)}>×</button>
              </figure>
            ))}
          </div>
        ) : null}
        <p className="request-wizard__photo-hint">Хорошие фото: в полный рост, морда крупно, рядом с привычной переноской</p>
      </div>
      <StepError message={error} />
    </StepFrame>
  );
}

function petTypeLabel(draft: RequestDraft): string {
  if (draft.petType === 'other') return draft.customType || 'Другое';
  return PET_TYPES.find((item) => item.value === draft.petType)?.label ?? '';
}

function moneyLabel(draft: RequestDraft): string {
  if (draft.budgetMode === 'offers') return 'Предложат перевозчики';
  const min = draft.budgetMin.trim();
  const max = draft.budgetMax.trim();
  const symbol = getDisplayCurrency() === 'EUR' ? '€' : '₽';
  if (min && max) return `${min}–${max} ${symbol}`;
  if (min) return `от ${min} ${symbol}`;
  if (max) return `до ${max} ${symbol}`;
  return 'Не указан';
}

function ReviewStep({
  busy,
  draft,
  error,
  onPublish,
}: WizardStepProps & { busy: boolean; onPublish: () => Promise<void> }) {
  const petRows: [string, ReactNode][] = [["Тип", <>
    <span>{petTypeLabel(draft)}</span>
    {draft.breed.trim() ? <> · {createElement('span', { 'data-i18n-skip': true }, draft.breed.trim())}</> : null}
  </>]];
  if (draft.weightKg.trim()) petRows.push(["Вес", `${draft.weightKg} кг`]);
  if (draft.age.trim()) petRows.push(["Возраст", draft.age]);
  return (
    <StepFrame
      node="2736:37054"
      title="Всё верно — публикуем?"
      description="Проверьте детали, любой блок можно поправить — кнопка «Изменить» рядом с каждым"
      actions={<WizardButton className="request-wizard__publish" disabled={busy} onClick={() => void onPublish()}>{busy ? 'Публикуем…' : 'Опубликовать и получить отклики'}</WizardButton>}
    >
      <div className="request-wizard__summary-list">
        <SummaryCard title="Маршрут" editScreen="new-request-step-1" rows={[["Откуда", draft.fromCity || 'Москва (адрес — в чате)'], ["Куда", draft.toCity || 'Сочи (адрес — в чате)']]} />
        <SummaryCard title="Питомец" editScreen="new-request-step-2" rows={petRows} />
        <SummaryCard title="Сроки" editScreen="new-request-step-3" rows={[["Забор", draft.pickupNotBefore || '15–17 июня'], ["Доставка", draft.deliveryNotAfter || 'до 20 июня']]} />
        <SummaryCard title="Условия" editScreen="new-request-step-4" rows={[["Переноска", draft.carrierBox === 'own_box' ? 'Своя' : 'От перевозчика'], ["Документы", draft.documentsStatus === 'ready' ? 'Готовы' : 'Оформляются']]} />
        <SummaryCard title="Бюджет" editScreen="new-request-step-6" rows={[["Диапазон", draft.budgetMin || draft.budgetMax || draft.budgetMode === 'offers' ? moneyLabel(draft) : `12 000–18 000 ${getDisplayCurrency() === 'EUR' ? '€' : '₽'}`]]} />
      </div>
      <StepError message={error} />
    </StepFrame>
  );
}

function SummaryCard({ editScreen, rows, title }: { editScreen: ScreenId; rows: [string, ReactNode][]; title: string }) {
  return (
    <section className="request-wizard__summary-card">
      <div className="request-wizard__summary-header">
        <h2>{title}</h2>
        <Button kind="tertiary" size="medium" onClick={() => navigate(editScreen)}>Изменить</Button>
      </div>
      {rows.map(([label, value]) => (
        <div className="request-wizard__summary-row" key={label}>
          <span>{label}</span>
          <strong>{value}</strong>
        </div>
      ))}
    </section>
  );
}

interface RegistrationStepProps extends WizardStepProps {
  busy: boolean;
  setBusy: (busy: boolean) => void;
  setAuthenticatedUser: (user: SessionUser) => void;
  finalizeRequest: () => Promise<void>;
  onRegistrationComplete?: (emailSent: boolean) => void;
}

function RegistrationHeader({ part, label }: { part: number; label: string }) {
  return (
    <div className="request-wizard__registration-header">
      <span>Шаг 9</span><i aria-hidden="true" /><span>Регистрация ({part}/5)</span><i aria-hidden="true" /><span>{label}</span>
    </div>
  );
}

function EmailRegistrationStep({
  busy,
  draft,
  error,
  finalizeRequest,
  setAuthenticatedUser,
  setBusy,
  setError,
  updateDraft,
}: RegistrationStepProps) {
  const [accountExists, setAccountExists] = useState(false);
  const [password, setPassword] = useState('');

  const continueWithEmail = async () => {
    const email = draft.email.trim();
    if (!email) {
      setError('Введите email.');
      return;
    }
    if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email)) {
      setError('Введите корректный email.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const result = await api<{ exists: boolean }>('/api/auth/start/', {
        method: 'POST',
        body: { email },
      });
      if (result.exists) setAccountExists(true);
      else navigate('new-request-step-9-role');
    } catch (nextError) {
      setError(messageFromError(nextError));
    } finally {
      setBusy(false);
    }
  };

  const login = async () => {
    if (!password) {
      setError('Введите пароль.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const user = await api<SessionUser>('/api/auth/login/', {
        method: 'POST',
        body: { email: draft.email.trim(), password },
      });
      if (!user.has_customer_profile) throw new Error('У этого аккаунта нет профиля заказчика.');
      setAuthenticatedUser(user);
      await finalizeRequest();
    } catch (nextError) {
      setError(messageFromError(nextError));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="request-wizard__registration" data-figma-node="2736:39017">
      <RegistrationHeader part={1} label="email" />
      <div className="request-wizard__registration-copy">
        <h1>{accountExists ? 'Войдите, чтобы сохранить заявку' : 'Сохраним заявку — введите email'}</h1>
        <p>{accountExists ? 'Аккаунт с таким email уже существует. Введите пароль — заявка не потеряется.' : 'Если у вас уже есть аккаунт — попросим пароль. Если нет — за минуту создадим новый, заявка не потеряется.'}</p>
      </div>
      <div className="request-wizard__registration-fields">
        <div className="request-wizard__role-banner">
          <span>{accountExists ? <>Аккаунт найден: <strong>{draft.email}</strong></> : <>Регистрируетесь как: <strong>Заказчик</strong></>}</span>
          <Button icon="right" kind="tertiary" size="medium" onClick={() => accountExists ? setAccountExists(false) : navigate('carrier-rega-email')}>
            {accountExists ? 'Изменить email' : 'Я перевозчик'}
          </Button>
        </div>
        {accountExists ? (
          <Input density="compact" label="Пароль" type="password" autoComplete="current-password" placeholder="Введите пароль" value={password} onChange={(event) => setPassword(event.target.value)} />
        ) : (
          <Input density="compact" label="Email" type="email" autoComplete="email" placeholder="anna@example.com" value={draft.email} onChange={(event) => updateDraft({ email: event.target.value })} />
        )}
      </div>
      <Button className="request-wizard__registration-submit" disabled={busy} onClick={() => void (accountExists ? login() : continueWithEmail())}>
        {busy ? 'Проверяем…' : accountExists ? 'Войти и продолжить' : 'Продолжить'}
      </Button>
      <div className="request-wizard__social-block">
        <div className="request-wizard__divider"><span>или войти через</span></div>
        <SocialAuthButtons role="customer" flow="request" compact />
        <p>Продолжая, вы соглашаетесь с условиями оферты и обработкой персональных данных</p>
      </div>
      <StepError message={error} />
    </div>
  );
}

function AccountTypeStep({ draft, error, setError, updateDraft }: WizardStepProps) {
  return (
    <div className="request-wizard__registration request-wizard__registration--account" data-figma-node="2736:39544">
      <RegistrationHeader part={2} label="тип аккаунта" />
      <div className="request-wizard__registration-copy">
        <h1>Кто вы?</h1>
        <p>Это определит, какие поля будут в профиле</p>
      </div>
      <div className="request-wizard__role-banner request-wizard__role-banner--notice">Аккаунта с email {draft.email || 'anna@example.com'} ещё нет — создадим новый</div>
      <div className="request-wizard__account-tiles">
        <Tile active={draft.accountType === 'person'} title="Частное лицо" description="Я владелец животного и хочу его перевезти." onClick={() => { updateDraft({ accountType: 'person' }); setError(''); navigate('new-request-step-9-name'); }} />
        <Tile active={draft.accountType === 'company'} title="Компания" description="Питомник, приют, зооклиника или другая организация." onClick={() => { updateDraft({ accountType: 'company' }); setError(''); navigate('new-request-step-9-name-company'); }} />
      </div>
      <StepError message={error} />
    </div>
  );
}

function ContactRegistrationStep({
  busy,
  company,
  draft,
  error,
  setBusy,
  setError,
  updateDraft,
}: WizardStepProps & { busy: boolean; company: boolean; setBusy: (busy: boolean) => void }) {
  const sendCode = async () => {
    if (!draft.phone.trim()) {
      setError('Введите номер телефона.');
      return;
    }
    if (company ? (!draft.companyName.trim() || !draft.contactName.trim()) : !draft.firstName.trim()) {
      setError(company ? 'Укажите организацию и контактное лицо.' : 'Укажите ваше имя.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const result = await api<{ debug_code?: string }>('/api/auth/sms/send/', {
        method: 'POST',
        body: { phone: draft.phone.trim(), purpose: 'register' },
      });
      updateDraft({ debugSmsCode: result.debug_code ?? '', smsCode: result.debug_code ?? '' });
      navigate('new-request-step-9-sent');
    } catch (nextError) {
      setError(messageFromError(nextError));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="request-wizard__registration request-wizard__registration--contact" data-figma-node={company ? '2736:40536' : '2736:40055'}>
      <RegistrationHeader part={3} label="контакты" />
      <div className="request-wizard__registration-copy">
        <h1>{company ? 'Расскажите о компании' : 'Расскажите о себе'}</h1>
        <p>{company ? 'Компания · одно дополнительное поле — название организации' : 'Частное лицо · имя нужно перевозчику для связи, телефон — для оперативных уведомлений'}</p>
      </div>
      <div className="request-wizard__registration-inputs">
        {company ? (
          <>
            <Input density="compact" label="Название компании" placeholder="Например, Питомник «Бэст»" value={draft.companyName} onChange={(event) => updateDraft({ companyName: event.target.value })} />
            <Input density="compact" label="Ваше имя (контактного лица)" placeholder="Анна" value={draft.contactName} onChange={(event) => updateDraft({ contactName: event.target.value })} />
          </>
        ) : (
          <Input density="compact" label="Ваше имя" placeholder="Анна" value={draft.firstName} onChange={(event) => updateDraft({ firstName: event.target.value })} />
        )}
        <InternationalPhoneInput density="compact" label="Телефон" value={draft.phone} onChange={(phone) => updateDraft({ phone })} />
        <p className="request-wizard__privacy-hint">Передаём перевозчику только после того, как вы примете отклик</p>
      </div>
      <Button className="request-wizard__registration-submit" disabled={busy} onClick={() => void sendCode()}>{busy ? 'Отправляем…' : 'Создать аккаунт'}</Button>
      <p className="request-wizard__profile-hint">{company ? 'Реквизиты и документы организации можно загрузить позже в профиле' : 'Документы и доп. данные можно заполнить позже в профиле'}</p>
      <StepError message={error} />
    </div>
  );
}

function SmsCodeInputs({ code, onChange }: { code: string; onChange: (code: string) => void }) {
  const refs = useRef<Array<HTMLInputElement | null>>([]);
  return (
    <div className="request-wizard__code-row">
      {Array.from({ length: 6 }, (_, index) => (
        <input
          aria-label={`Цифра ${index + 1}`}
          inputMode="numeric"
          key={index}
          maxLength={1}
          ref={(node) => { refs.current[index] = node; }}
          value={code[index] ?? ''}
          onChange={(event) => {
            const value = event.target.value.replace(/\D/g, '').slice(-1);
            const chars = code.padEnd(6, ' ').split('');
            chars[index] = value || ' ';
            onChange(chars.join('').trimEnd());
            if (value && index < 5) refs.current[index + 1]?.focus();
          }}
          onKeyDown={(event) => {
            if (event.key === 'Backspace' && !code[index] && index > 0) refs.current[index - 1]?.focus();
          }}
        />
      ))}
    </div>
  );
}

function SmsStep({ busy, draft, error, setBusy, setError, updateDraft }: WizardStepProps & { busy: boolean; setBusy: (busy: boolean) => void }) {
  const verify = async () => {
    if (draft.smsCode.replace(/\s/g, '').length !== 6) {
      setError('Введите шестизначный код.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      await api('/api/auth/sms/verify/', { method: 'POST', body: { phone: draft.phone, purpose: 'register', code: draft.smsCode } });
      navigate('new-request-step-9-password');
    } catch (nextError) {
      setError(localizeSmsError(messageFromError(nextError)));
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    setBusy(true);
    setError('');
    try {
      const result = await api<{ debug_code?: string }>('/api/auth/sms/send/', { method: 'POST', body: { phone: draft.phone, purpose: 'register' } });
      updateDraft({ debugSmsCode: result.debug_code ?? '', smsCode: result.debug_code ?? '' });
    } catch (nextError) {
      setError(messageFromError(nextError));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="request-wizard__registration request-wizard__registration--sms" data-figma-node="2736:41077">
      <RegistrationHeader part={4} label="подтверждение телефона" />
      <div className="request-wizard__registration-copy">
        <h1>Введите код из SMS</h1>
        <p>Отправили на {draft.phone || '+7 925 ***-**-12'}. Подтверждаем телефон сейчас — email подтвердите потом в профиле.</p>
      </div>
      <SmsCodeInputs code={draft.smsCode} onChange={(smsCode) => updateDraft({ smsCode })} />
      <div className="request-wizard__sms-actions">
        <Button disabled={busy} onClick={() => void verify()}>{busy ? 'Проверяем…' : 'Подтвердить'}</Button>
        <Button kind="tertiary" size="medium" onClick={() => void resend()}>Не пришёл код? Отправить снова через 0:42</Button>
        <StepError message={error} />
        <Button kind="tertiary" size="medium" onClick={() => navigate(draft.accountType === 'company' ? 'new-request-step-9-name-company' : 'new-request-step-9-name')}>Сменить номер телефона</Button>
      </div>
    </div>
  );
}

function PasswordStep({
  busy,
  draft,
  error,
  finalizeRequest,
  onRegistrationComplete,
  setAuthenticatedUser,
  setBusy,
  setError,
}: RegistrationStepProps) {
  const [password, setPassword] = useState('');
  const [repeatPassword, setRepeatPassword] = useState('');
  const [registered, setRegistered] = useState(false);

  const register = async () => {
    if (registered) {
      setBusy(true);
      setError('');
      try {
        await finalizeRequest();
      } catch (nextError) {
        setError(messageFromError(nextError));
      } finally {
        setBusy(false);
      }
      return;
    }
    if (password.length < 8 || !/[A-ZА-Я]/.test(password) || !/\d/.test(password)) {
      setError('Пароль: минимум 8 символов, одна заглавная буква и одна цифра.');
      return;
    }
    if (password !== repeatPassword) {
      setError('Пароли не совпадают.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const user = await api<SessionUser & { verification_email_sent?: boolean }>('/api/auth/register/', {
        method: 'POST',
        body: {
          role: 'customer',
          email: draft.email,
          password,
          phone: draft.phone,
          sms_code: draft.smsCode,
          account_type: draft.accountType,
          first_name: draft.firstName,
          last_name: draft.lastName,
          company_name: draft.companyName,
          contact_name: draft.contactName,
        },
      });
      setAuthenticatedUser(user);
      setRegistered(true);
      onRegistrationComplete?.(Boolean(user.verification_email_sent));
      await finalizeRequest();
    } catch (nextError) {
      setError(messageFromError(nextError));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      className="request-wizard__registration request-wizard__registration--password"
      data-figma-node="2736:41628"
      onSubmit={(e) => {
        e.preventDefault();
        void register();
      }}
    >
      <RegistrationHeader part={5} label="пароль" />
      <div className="request-wizard__registration-copy">
        <h1>Придумайте пароль</h1>
        <p>Понадобится для входа с других устройств</p>
      </div>
      <div className="request-wizard__success-box">
        <strong>✓ Телефон подтверждён: {draft.phone || '+7 925 ***-**-12'}</strong>
        <span>После создания аккаунта отправим подтверждение на {draft.email || 'anna@example.com'}</span>
      </div>
      <div className="request-wizard__password-fields">
        <Input density="compact" label="Пароль" type="password" autoComplete="new-password" placeholder="Минимум 8 символов" value={password} onChange={(event) => setPassword(event.target.value)} />
        <p>Латиница, цифры, минимум одна заглавная буква</p>
        <Input density="compact" label="Повторите пароль" type="password" autoComplete="new-password" placeholder="Повторите" value={repeatPassword} onChange={(event) => setRepeatPassword(event.target.value)} />
      </div>
      <Button className="request-wizard__registration-submit" type="submit" disabled={busy}>{busy ? 'Сохраняем…' : registered ? 'Повторить публикацию заявки' : 'Сохранить и опубликовать заявку'}</Button>
      <StepError message={error} />
    </form>
  );
}

function SuccessStep({
  accountCreated,
  draft,
  verificationEmailSent,
}: {
  accountCreated: boolean;
  draft: RequestDraft;
  verificationEmailSent: boolean;
}) {
  return (
    <div className="request-wizard__success" data-figma-node="2736:37889">
      <div className="request-wizard__copy">
        <h1>Готово! Заявка опубликована</h1>
        <p>Перевозчики уже видят её в общей ленте. Первые отклики обычно приходят в течение часа — придёт уведомление.</p>
        {accountCreated ? (
          <p className="request-wizard__account-created">
            {verificationEmailSent
              ? `Аккаунт заказчика создан. На ${draft.email} отправлена ссылка подтверждения. Для следующего входа используйте этот email и пароль, который вы задали.`
              : `Аккаунт заказчика создан. Для следующего входа используйте ${draft.email} и пароль, который вы задали. Письмо подтверждения можно отправить повторно из профиля.`}
          </p>
        ) : null}
      </div>
      <section className="request-wizard__success-summary">
        <div><span>Номер заявки</span><strong>{draft.publicCode || (draft.requestId ? `#${draft.requestId}` : '#PM-2841')}</strong></div>
        <div>
          <span>Маршрут</span>
          <strong className="request-wizard__success-route">
            <span>{draft.fromCity || 'Москва'}</span>
            <img src="/figma/assets/landing-order-route-arrow.svg" alt="" aria-hidden="true" />
            <span>{draft.toCity || 'Сочи'}</span>
          </strong>
        </div>
        <div><span>Статус</span><Tag className="request-wizard__success-status" type="Verification" icon="no" label="Открыта · ждём отклики" /></div>
      </section>
      <div className="request-wizard__success-actions">
        <WizardButton onClick={() => { window.location.hash = `#request-detail?id=${draft.requestId ?? ''}`; }}>Открыть заявку</WizardButton>
        <WizardButton kind="secondary" onClick={() => navigate('my-requests')}>К моим заявкам</WizardButton>
      </div>
    </div>
  );
}

export function RequestWizardScreen({ screen }: { screen: ScreenId }) {
  const { completeDraft, draft, replaceDraft, updateDraft } = useRequestDraft();
  const { setAuthenticatedUser, status, user } = useAuth();
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [registrationResult, setRegistrationResult] = useState<{ completed: boolean; emailSent: boolean }>({
    completed: false,
    emailSent: false,
  });

  useEffect(() => setError(''), [screen]);

  useEffect(() => {
    const provider = hashQuery().get('oauth');
    if (provider !== 'yandex' && provider !== 'vk' && provider !== 'google' && provider !== 'apple') return;
    const loadPending = provider === 'google' || provider === 'apple' ? () => loadPendingOidc(provider) : provider === 'vk' ? loadPendingVkRegistration : loadPendingYandexRegistration;
    void loadPending()
      .then((pending) => updateDraft({
        email: pending.email,
        firstName: pending.first_name,
        lastName: pending.last_name,
      }))
      .catch(() => undefined);
  }, [screen, updateDraft]);

  useEffect(() => {
    if (screen === 'new-request-step-1' && draft.publicCode) {
      replaceDraft(emptyRequestDraft());
    }
  }, [draft.publicCode, replaceDraft, screen]);

  const finalizeRequest = useCallback(async () => {
    const pendingPhotos = draft.photoAssetIds.length ? [] : await getPendingPhotos(draft.photoKeys);
    const uploadedIds = draft.photoAssetIds.length
      ? draft.photoAssetIds
      : await uploadRequestPhotos(pendingPhotos);
    const saved = await saveRequestOnServer(draft, uploadedIds);
    updateDraft({ requestId: saved.id, photoAssetIds: uploadedIds });
    const published = await publishRequest(saved.id);
    completeDraft({
      requestId: published.id,
      publicCode: published.public_code ?? '',
      photoAssetIds: uploadedIds,
      photoKeys: [],
    });
    await clearPendingPhotos(draft.photoKeys).catch(() => undefined);
    navigate('new-request-step-10');
  }, [completeDraft, draft, updateDraft]);

  const publishFromReview = useCallback(async () => {
    if (status !== 'authenticated' || !user) {
      navigate('new-request-step-9');
      return;
    }
    if (!user.has_customer_profile) {
      setError('Для публикации нужен профиль заказчика.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      await finalizeRequest();
    } catch (nextError) {
      setError(messageFromError(nextError));
    } finally {
      setBusy(false);
    }
  }, [finalizeRequest, status, user]);

  const common: WizardStepProps = { draft, error, setError, updateDraft };
  let content: ReactNode = null;
  if (screen === 'new-request-step-1') content = <RouteStep {...common} />;
  if (screen === 'new-request-step-2') content = <PetStep {...common} />;
  if (screen === 'new-request-step-3') content = <DateStep {...common} />;
  if (screen === 'new-request-step-4') content = <ConditionsStep {...common} />;
  if (screen === 'new-request-step-5') content = <ContactStep {...common} />;
  if (screen === 'new-request-step-6') content = <BudgetStep {...common} />;
  if (screen === 'new-request-step-7') content = <PhotoStep {...common} />;
  if (screen === 'new-request-step-8') content = <ReviewStep {...common} busy={busy} onPublish={publishFromReview} />;
  if (screen === 'new-request-step-9') content = <EmailRegistrationStep {...common} busy={busy} setBusy={setBusy} setAuthenticatedUser={setAuthenticatedUser} finalizeRequest={finalizeRequest} />;
  if (screen === 'new-request-step-9-role') content = <AccountTypeStep {...common} />;
  if (screen === 'new-request-step-9-name') content = <ContactRegistrationStep {...common} busy={busy} setBusy={setBusy} company={false} />;
  if (screen === 'new-request-step-9-name-company') content = <ContactRegistrationStep {...common} busy={busy} setBusy={setBusy} company />;
  if (screen === 'new-request-step-9-sent') content = <SmsStep {...common} busy={busy} setBusy={setBusy} />;
  if (screen === 'new-request-step-9-password') content = <PasswordStep {...common} busy={busy} setBusy={setBusy} setAuthenticatedUser={setAuthenticatedUser} finalizeRequest={finalizeRequest} onRegistrationComplete={(emailSent) => setRegistrationResult({ completed: true, emailSent })} />;
  if (screen === 'new-request-step-10') content = <SuccessStep accountCreated={registrationResult.completed} draft={draft} verificationEmailSent={registrationResult.emailSent} />;

  return <WizardShell screen={screen}>{content}</WizardShell>;
}
